import { useEffect, useState } from 'react';
import Sheet from 'react-modal-sheet';
import clsx from 'clsx';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorageKeys.ts';
import QuestionnaireCategoriesContent from './QuestionnaireCategoriesContent.tsx';
import QuestionnaireSuccess from './QuestionnaireSuccess.tsx';
import QuestionnairePartnersContent from './QuestionnairePartnersContent.tsx';
import { useMutation } from '@tanstack/react-query';
import {
  InterestAnalyticsItem,
  PartnerFavoriteSave,
  PartnerWithCategory,
  RequestData,
  YSH_GTM_EVENTS
} from '../types/types.ts';
import { callPublicAPI } from '../utils/api.ts';
import { isUserLoggedIn } from '../utils/auth.ts';
import { pushToAnalytics } from '../utils/analytics.ts';
import { useFetchCategories } from '../hooks/useFetchCategories.ts';
import '../scss/questionnaire-modal.scss';
import { queryClient } from '../config/queryClientFactory.ts';
import { QUERY_KEYS } from '../constants/queryKeys.ts';

const QUESTIONNAIRE_PAGES = {
  CATEGORIES: 'CATEGORIES',
  PARTNERS: 'PARTNERS',
  SUCCESS: 'SUCCESS'
};

export const QuestionnaireModal = () => {
  const { data: categories } = useFetchCategories(false);
  const [isQuestionnairePopupActive, setIsQuestionnairePopupActive] = useState(false);
  const [currentQuestionnairePage, setCurrentQuestionnairePage] = useState(QUESTIONNAIRE_PAGES.CATEGORIES);
  const [userSelectedCategories, setUserSelectedCategories] = useState<string[]>([]);
  const [userSelectedPartners, setUserSelectedPartners] = useState<PartnerWithCategory[]>([]);

  const saveSelectedCategories = useMutation({
    mutationFn: async () => {
      const data: RequestData = {
        categories: userSelectedCategories
      };
      return await callPublicAPI<string[]>('POST', '/user-selected-categories', null, data);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.GET_USER_SELECTED_CATEGORY_IDS]
      });
    },
    onError: () => {
      alert('Žao nam je, nismo uspeli da sačuvamo kategorije');
    }
  });

  const saveSelectedPartners = useMutation<PartnerFavoriteSave>({
    mutationFn: async () => {
      const data: RequestData = {
        partners: userSelectedPartners.map(partner => partner.id)
      };
      const response = await callPublicAPI<PartnerFavoriteSave>('POST', `/user-selected-partners`, null, data);
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.GET_SELECTED_PARTNERS]
      });
    },
    onError: () => {
      alert('Žao nam je, nismo uspeli da sačuvamo partnere');
    }
  });

  const shouldShowQuestionnaireModal = () => {
    return isUserLoggedIn() && localStorage.getItem(LOCAL_STORAGE_KEYS.SHOULD_SHOW_QUESTIONNAIRE_MODAL) === 'true';
  };

  const setAsSeen = () => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.SHOULD_SHOW_QUESTIONNAIRE_MODAL, 'false');
  };

  const onCloseQuestionnairePopup = () => {
    setAsSeen();
    setIsQuestionnairePopupActive(false);
  };

  const handleSubmitCategories = () => {
    const items: InterestAnalyticsItem[] = [];
    let itemIndex = 1;

    userSelectedCategories.forEach(id => {
      const category = categories?.find(cat => cat.id === id);

      if (category) {
        const item: InterestAnalyticsItem = {
          index: itemIndex,
          item_id: category.name,
          item_name: category.name,
          item_category: 'all_categories',
          item_category2: ''
        };

        itemIndex++;
        items.push(item);
      }
    });

    pushToAnalytics({
      eventName: YSH_GTM_EVENTS.SAVE_INTERESTS,
      placement: 'pop_up_questionaire',
      data: {
        items
      }
    });

    saveSelectedCategories.mutate();
    setCurrentQuestionnairePage(QUESTIONNAIRE_PAGES.PARTNERS);
  };

  const handleSubmitPartners = () => {
    const items: InterestAnalyticsItem[] = [];
    let itemIndex = 1;

    userSelectedPartners.forEach(partner => {
      const item: InterestAnalyticsItem = {
        index: itemIndex,
        item_id: partner.name,
        item_name: partner.name,
        item_category: '',
        item_category2: partner.category
      };

      itemIndex++;
      items.push(item);
    });

    pushToAnalytics({
      eventName: YSH_GTM_EVENTS.SAVE_INTERESTS,
      placement: 'pop_up_questionaire',
      data: {
        items
      }
    });

    saveSelectedPartners.mutate();
    setCurrentQuestionnairePage(QUESTIONNAIRE_PAGES.SUCCESS);
  };

  useEffect(() => {
    if (shouldShowQuestionnaireModal()) {
      setIsQuestionnairePopupActive(true);
    }
  }, []);

  return (
    <>
      <Sheet
        isOpen={isQuestionnairePopupActive}
        onClose={() => null}
        disableDrag={true}
        className={clsx(
          'questionnaire-modal',
          currentQuestionnairePage === QUESTIONNAIRE_PAGES.CATEGORIES && 'questionnaire-modal--categories',
          currentQuestionnairePage === QUESTIONNAIRE_PAGES.PARTNERS && 'questionnaire-modal--partners',
          currentQuestionnairePage === QUESTIONNAIRE_PAGES.SUCCESS && 'questionnaire-modal--success'
        )}
      >
        <Sheet.Container>
          <Sheet.Content>
            {currentQuestionnairePage === QUESTIONNAIRE_PAGES.CATEGORIES && (
              <QuestionnaireCategoriesContent
                categories={categories || []}
                handleSubmitCategories={handleSubmitCategories}
                userSelectedCategories={userSelectedCategories}
                setUserSelectedCategories={setUserSelectedCategories}
              />
            )}
            {currentQuestionnairePage === QUESTIONNAIRE_PAGES.PARTNERS && (
              <QuestionnairePartnersContent
                userSelectedCategories={userSelectedCategories}
                handleSubmitPartners={handleSubmitPartners}
                userSelectedPartners={userSelectedPartners}
                setUserSelectedPartners={setUserSelectedPartners}
              />
            )}
            {currentQuestionnairePage === QUESTIONNAIRE_PAGES.SUCCESS && (
              <QuestionnaireSuccess onClose={onCloseQuestionnairePopup} />
            )}
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={() => null} />
      </Sheet>
    </>
  );
};

export default QuestionnaireModal;
